/**
 * permissionDuration.ts — shared display helpers for permission-type
 * (leave_type_code === "permission") request durations and time windows.
 *
 * Problem: DayBreakdown.counts_as is expressed in DAYS (a fraction of a
 * workday — e.g. a 1-hour permission on an 8h workday is counts_as=0.125),
 * not hours. Several screens (My Leave Detail, Leave Review, Approvals
 * Inbox) were labelling this value "h" (hours) or showing the raw fraction
 * as if it were a day count — both misleading for permission requests.
 * DayBreakdown also carries start_time/end_time in raw "HH:MM" 24h format,
 * inconsistent with the "h:mm AM/PM" format used elsewhere in the app (e.g.
 * QuickLinksCard.tsx's formatTime).
 *
 * These helpers derive the correct hour/minute duration directly from the
 * time window (never from counts_as) and format times consistently.
 */

/** Minimal shape needed from DayBreakdown — avoids a hard dependency on the
 *  full leave-v2-api DayBreakdown type so this stays usable from any screen. */
export interface TimeWindowDay {
  start_time?: string;
  end_time?: string;
}

/**
 * Formats a "HH:MM" 24h time (as returned by the backend for permission-type
 * day windows) as "h:mm AM/PM". Returns the raw string unparsed on failure
 * rather than throwing.
 */
export function fmtTime12(hhmm: string | null | undefined): string {
  if (!hhmm) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  const h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ampm}`;
}

function formatMinutes(totalMinutes: number): string {
  const hrs = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} min`;
  if (hrs > 0) return `${hrs} hr`;
  return `${mins} min`;
}

/**
 * Sums each day's start_time→end_time window into a total "X hr Y min"
 * string. Returns null when no day carries a parseable time window — the
 * caller should fall back to a days-based total in that case (e.g. for a
 * non-permission leave type accidentally routed here, or legacy rows with no
 * recorded time window).
 */
export function formatPermissionDuration(days: TimeWindowDay[]): string | null {
  let totalMinutes = 0;
  let sawWindow = false;
  for (const d of days) {
    if (!d.start_time || !d.end_time) continue;
    const sm = /^(\d{1,2}):(\d{2})/.exec(d.start_time);
    const em = /^(\d{1,2}):(\d{2})/.exec(d.end_time);
    if (!sm || !em) continue;
    sawWindow = true;
    const startMin = parseInt(sm[1], 10) * 60 + parseInt(sm[2], 10);
    let endMin = parseInt(em[1], 10) * 60 + parseInt(em[2], 10);
    if (endMin <= startMin) endMin += 24 * 60; // window crosses midnight (e.g. Continue WFH)
    totalMinutes += endMin - startMin;
  }
  if (!sawWindow) return null;
  return formatMinutes(totalMinutes);
}

/**
 * Full duration label for a permission request: prefers the day time
 * window (formatPermissionDuration), and falls back to totalDays * 8h
 * (the same default workday-hours the backend uses — see
 * leaveTypeWorkdayHours in request_usecase.go) when no day carries a
 * start_time/end_time — e.g. rows created before the time window was
 * recorded, or a list endpoint that doesn't hydrate per-day fields.
 * Only falls back to a raw "Xd" label when totalDays itself is missing.
 */
export function permissionDurationLabel(days: TimeWindowDay[], totalDays: number | null | undefined): string {
  const fromWindow = formatPermissionDuration(days);
  if (fromWindow) return fromWindow;
  if (totalDays != null && totalDays > 0) return formatMinutes(totalDays * 8 * 60);
  return '-';
}
